import { useDark, useToggle, useCssVar } from '@vueuse/core'
import { ref, watch } from 'vue'

/**
 * 主题模式类型定义
 */
type ThemeMode = 'light' | 'dark'

/**
 * 主题管理组合式函数
 * 基于 VueUse 的 useDark 和 useCssVar 实现
 */
export function useTheme() {
  // 使用 VueUse 的 useDark 管理暗黑模式状态
  // 配置：默认暗黑模式，存储键名为 panda-creative-theme
  const isDark = useDark({
    storageKey: 'panda-creative-theme',
    valueDark: 'dark',
    valueLight: 'light',
    initialValue: 'dark', // 默认暗黑模式
    onChanged: (dark: boolean, defaultHandler) => {
      // 自定义主题切换逻辑
      const root = document.documentElement
      
      if (dark) {
        root.setAttribute('data-theme', 'dark')
        root.classList.add('dark')
      } else {
        root.removeAttribute('data-theme')
        root.classList.remove('dark')
      }
      
      // 调用默认处理器
      defaultHandler(dark ? 'dark' : 'light')
    }
  })

  // 主题切换函数
  const toggleDark = useToggle(isDark)

  // 主题模式响应式引用
  const themeMode = ref<ThemeMode>(isDark.value ? 'dark' : 'light')

  // 监听暗黑模式变化，同步主题模式
  watch(isDark, (newIsDark) => {
    themeMode.value = newIsDark ? 'dark' : 'light'
  })

  /**
   * 设置特定主题
   * @param mode - 要设置的主题模式
   */
  const setTheme = (mode: ThemeMode): void => {
    isDark.value = mode === 'dark'
  }

  /**
   * 获取当前主题模式
   * @returns 当前主题模式
   */
  const getCurrentTheme = (): ThemeMode => {
    return themeMode.value
  }

  /**
   * 动态修改 CSS 变量
   * @param variableName - CSS 变量名
   * @param value - 变量值
   */
  const setCssVariable = (variableName: string, value: string): void => {
    const cssVar = useCssVar(variableName)
    cssVar.value = value
  }

  /**
   * 批量设置 CSS 变量
   * @param variables - CSS 变量键值对
   */
  const setCssVariables = (variables: Record<string, string>): void => {
    Object.entries(variables).forEach(([key, value]) => {
      setCssVariable(key, value)
    })
  }

  return {
    // 状态
    isDark,
    themeMode,
    
    // 操作
    toggleTheme: toggleDark,
    setTheme,
    getCurrentTheme,
    
    // CSS 变量操作
    setCssVariable,
    setCssVariables
  }
}

/**
 * 全局主题实例
 */
let globalThemeInstance: ReturnType<typeof useTheme> | null = null

/**
 * 获取或创建全局主题实例
 * @returns 全局主题实例
 */
export function useGlobalTheme(): ReturnType<typeof useTheme> {
  if (!globalThemeInstance) {
    globalThemeInstance = useTheme()
  }
  return globalThemeInstance
}